import { useCallback, useEffect, useState } from 'react';
import sucursalesVentaService, { SucursalVenta } from '../services/sucursalesVentaService';
import { storedNullableNumber } from '../utils/tesoreriaFiltrosStorage';

export function readGlobalNroSuc(): number | null {
  try {
    const raw = localStorage.getItem('nroSuc') || localStorage.getItem('sucursal') || '0';
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

export function persistGlobalNroSuc(nroSuc: number): void {
  if (!Number.isFinite(nroSuc) || nroSuc <= 0) return;
  try {
    localStorage.setItem('nroSuc', String(nroSuc));
    localStorage.setItem('sucursal', String(nroSuc));
  } catch {}
}

export function clearGlobalNroSuc(): void {
  try {
    localStorage.removeItem('nroSuc');
    localStorage.removeItem('sucursal');
  } catch {}
}

/** Valor inicial del filtro: storage de página si existe; si no, sucursal global. */
export function resolveInitialFiltroNroSuc(
  stored: object = {},
  key = 'filtroNroSuc',
): number | null {
  const record = stored as Partial<Record<string, unknown>>;
  if (key in record) {
    return storedNullableNumber(record, key, null);
  }
  return readGlobalNroSuc();
}

export function useSucursalesVentaFiltro(initialNroSuc?: number | null) {
  const [filtroNroSuc, setFiltroNroSucState] = useState<number | null>(() =>
    initialNroSuc !== undefined ? initialNroSuc : readGlobalNroSuc(),
  );
  const [sucursales, setSucursales] = useState<SucursalVenta[]>([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoadingSucursales(true);
        const result = await sucursalesVentaService.getSucursalesVenta({
          pageNumber: 1,
          pageSize: 100,
        });
        if (!cancelled) {
          setSucursales([...(result.items ?? [])].sort((a, b) => a.nroSuc - b.nroSuc));
        }
      } catch {
        if (!cancelled) setSucursales([]);
      } finally {
        if (!cancelled) setLoadingSucursales(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Mantener sucursal válida en la lista; null = "Todas" (válido para filtros)
  useEffect(() => {
    if (loadingSucursales || sucursales.length === 0) return;

    const inList = (n: number) =>
      Number.isFinite(n) && n > 0 && sucursales.some((s) => s.nroSuc === n);

    setFiltroNroSucState((current) => {
      if (current == null) return current;
      if (inList(current)) return current;
      const stored = readGlobalNroSuc();
      if (stored != null && inList(stored)) return stored;
      return null;
    });
  }, [loadingSucursales, sucursales]);

  const setFiltroNroSuc = useCallback((value: number | null) => {
    setFiltroNroSucState(value);
    if (value != null && value > 0) {
      persistGlobalNroSuc(value);
    } else {
      clearGlobalNroSuc();
    }
  }, []);

  return { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales };
}

export function resolveNroSucFiltro(nroSuc: number | null): number | undefined {
  return nroSuc != null && nroSuc > 0 ? nroSuc : undefined;
}

export const sucursalVentaSelectOptions = (sucursales: SucursalVenta[]) =>
  sucursales.map((s) => ({
    value: s.nroSuc,
    label: s.nombreSucursal?.trim()
      ? `${String(s.nroSuc).padStart(5, '0')} — ${s.nombreSucursal.trim()}`
      : String(s.nroSuc).padStart(5, '0'),
  }));
